import type { CatalogV2ListQuery } from '@skupreme/api-contracts/core';
import { CommonConstant } from '@utility/constants/common';
import type { ISortObject } from '../legacy-bridge';
import type { MarketSelection } from '../hooks/use-market-selection';
import { marketplaceIdsOf, withNoCountry } from './markets';
import type { CatalogMarketsQuery } from './markets-api';

export type CatalogListFilter = {
  page: number;
  limit: number;
  search: string;
  /* The table's sort object, spread into the query as its own keys. */
  sort: ISortObject;
};

export type CatalogListParams = Record<string, unknown>;

export const defaultCatalogFilter: CatalogListFilter = {
  limit: CommonConstant.defaultPageSize,
  page: 1,
  search: '',
  sort: {},
};

/* `state` narrows the list to one lifecycle, e.g. `Component`. */
export function buildCatalogListParams(
  filter: CatalogListFilter,
  regionId: unknown,
  state?: string,
): CatalogListParams {
  return {
    ...filter,
    ...filter.sort,
    marketplace_region_id: regionId,
    sort: null,
    search: (filter.search || '').trim(),
    ...(state ? { state } : {}),
  };
}

/* `-1` is the "all regions" option, which the API reads as no region. */
export function buildConflictsCountParams(regionId: unknown) {
  return regionId === -1 ? {} : { marketplace_region_id: regionId };
}

export type CatalogV2Filter = {
  /** Comma-separated lifecycle states. */
  state: string;
  type: string;
  connectionId: string;
  /** M2 listing hygiene: '' = the API's default (active items only), else `not_active` or `all`. */
  activity?: string;
};

export const defaultCatalogV2Filter: CatalogV2Filter = {
  state: 'active',
  type: '',
  connectionId: '',
  activity: '',
};

const V2_SORT_KEYS: CatalogV2ListQuery['sort_by'][] = [
  'title',
  'sku',
  'available',
];

/* M2: the `markets` filter, sent only while countries are picked and narrowing is on; the no-country stores ride along. */
export function marketsParam(
  market: MarketSelection | undefined,
): CatalogMarketsQuery {
  return market?.countries.length && market.onlyListed
    ? { markets: marketplaceIdsOf(withNoCountry(market.countries)).join(',') }
    : {};
}

/* The V2 query: no region, the sort as sort_by / sort_order, and the countries picked (M2). */
export function buildCatalogV2ListParams(
  filter: CatalogListFilter,
  v2Filter: CatalogV2Filter,
  market?: MarketSelection,
): CatalogListParams {
  const [sortBy, sortOrder] = Object.entries(filter.sort)[0] ?? [];
  const sorted = V2_SORT_KEYS.some(key => key === sortBy);
  const query: CatalogV2ListQuery = {
    page: filter.page,
    limit: filter.limit,
    search: filter.search.trim() || undefined,
    state: v2Filter.state,
    type: (v2Filter.type || undefined) as CatalogV2ListQuery['type'],
    connection_id: v2Filter.connectionId || undefined,
    activity: (v2Filter.activity ||
      undefined) as CatalogV2ListQuery['activity'],
    sort_by: sorted ? (sortBy as CatalogV2ListQuery['sort_by']) : undefined,
    sort_order: sorted ? sortOrder : undefined,
  };

  return { ...query, ...marketsParam(market) };
}
